import { memo } from 'react';
import { CardHeader } from '@/components/ui/card';
import { LocationHeaderProps } from '../types'; // Base props: location, isPotential, averageRating, ratingCount, handleClose
import { EditorInteractable } from '@/components/editor/EditorInteractable';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Store, Utensils, Beer, Building2, Loader2, ChevronDown } from 'lucide-react'; // Changed Restaurant to Utensils, added ChevronDown
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select"; // Added Select components
import { LocationType } from '@/types/firestore'; // Added LocationType import
import { TypeDisplayIcon } from './TypeDisplayIcon'; // Import the new TypeDisplayIcon
import StarRating from '@/components/ui/star-rating'; // Import StarRating component
// Removed imports: Button, icons (Star, BookmarkPlus, etc.), Popover, DropdownMenu, Dialog, Input, Checkbox, RatingStars, Textarea
// Removed store hooks and action imports (useAppDispatch, useAppSelector, map actions, userSpecific actions, auth selectors)
// Removed type imports: ReduxLocation, FirestoreLocation, VisitedLocation, RootState
// Removed GeoPoint, Timestamp from firebase/firestore as they are not directly used for display here

// New props would ideally go into LocationHeaderProps in types.ts
interface ExtendedLocationHeaderProps extends Omit<LocationHeaderProps, 'averageRating' | 'ratingCount'> {
  locationType?: LocationType;
  isUpdatingLocationType?: boolean;
  onLocationTypeChange?: (newType: LocationType) => void;
  isLocationTypeDisabled?: boolean;
  isVisited?: boolean;
  typeIconSize?: number; // Add new prop for icon size
  averageRating?: number;
  ratingCount?: number;
}

/**
 * Simplified header for the location info window.
 * Displays location name, address.
 * Location type is now a clickable icon with a dropdown to change type.
 * Action buttons are now handled in the parent component (LocationInfoWindowContent).
 */
export const LocationHeader = memo(({ 
  location, 
  isPotential, 
  locationType,
  isUpdatingLocationType,
  onLocationTypeChange,
  isLocationTypeDisabled,
  isVisited,
  typeIconSize = 62, // Default size
  averageRating = 0,
  ratingCount = 0,
}: ExtendedLocationHeaderProps) => {

  // All state, hooks, and handlers related to action buttons have been moved to LocationInfoWindowContent.

  const handleTypeSelect = (value: string) => {
    if (onLocationTypeChange) {
      onLocationTypeChange(value as LocationType);
    }
  };

  const hasRating = averageRating > 0 && ratingCount > 0;
  const chevronIconSize = 21; // h-7 w-7 translates to 1.75rem = 28px

  return (
    <CardHeader className="p-3 pb-2 flex flex-row justify-between items-start">
      {/* Left and Middle content: Type Icon, Title, Address */}
      <div className="flex items-start space-x-2 flex-1 min-w-0">
        {!isPotential && onLocationTypeChange && (
          <div className="flex items-center justify-center mr-4 mt-1">
            <Select
              value={locationType}
              onValueChange={handleTypeSelect}
              disabled={isLocationTypeDisabled || isUpdatingLocationType}
            >
              <SelectTrigger 
                className="relative p-0 border-0 rounded-md h-auto w-auto flex items-center justify-center focus:ring-0 focus:ring-offset-0 [&>svg]:hidden"
                aria-label="Change location type"
                style={{ width: `${typeIconSize}px`, height: `${typeIconSize}px` }}
              >
                <div className="relative w-full h-full flex items-center justify-center">
                  {isUpdatingLocationType ? (
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <TypeDisplayIcon 
                        type={locationType} 
                        isVisited={isVisited}
                        containerSize={typeIconSize}
                        size={Math.floor(typeIconSize * 0.5)} // Scale the emoji size proportionally
                      />
                      <ChevronDown 
                        size={chevronIconSize}
                        style={{ minWidth: `${chevronIconSize}px`, minHeight: `${chevronIconSize}px` }}
                        className="absolute -bottom-1 -right-1 text-primary-foreground pointer-events-none bg-primary rounded-full p-0.5 border-2 border-card shadow-sm"
                      />
                    </>
                  )}
                </div>
              </SelectTrigger>
              <SelectContent align="start">
                <SelectItem value="restaurant"><div className="flex items-center"><Utensils className="mr-2 h-4 w-4" />Restaurant</div></SelectItem>
                <SelectItem value="shop"><div className="flex items-center"><Store className="mr-2 h-4 w-4" />Shop</div></SelectItem>
                <SelectItem value="brewery"><div className="flex items-center"><Beer className="mr-2 h-4 w-4" />Brewery</div></SelectItem>
                <SelectItem value="other"><div className="flex items-center"><Building2 className="mr-2 h-4 w-4" />Other</div></SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex-1 min-w-0 pt-1">
          {isPotential ? (
            <>
              {hasRating && (
                <div className="flex items-center mb-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <StarRating 
                            value={averageRating} 
                            readOnly 
                            size="md"
                            className="space-x-0"
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs p-2">
                        <p>Rated {ratingCount} times</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
              <h3 className="font-medium text-lg truncate" title={location.name}>{location.name}</h3>
              <a 
                href={`https://maps.google.com/?q=${encodeURIComponent(location.address || location.name)}`} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-xs text-blue-500 hover:underline block truncate" 
                title={location.address || "View on Google Maps"}
              >
                {location.address || "No address"}
              </a>
            </>
          ) : (
            <EditorInteractable
              entityId={location.id || ''} // id should be present for non-potential
              entityType="location"
            >
              {hasRating && (
                <div className="flex items-center mb-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <StarRating 
                            value={averageRating} 
                            readOnly 
                            size="sm" 
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs p-2">
                        <div className="flex justify-between">
                          <span>Exact:</span>
                          <span>{averageRating}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="pr-3">Raters:</span>
                          <span>{ratingCount}</span>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
              <h3 className="font-medium text-lg truncate" title={location.name}>{location.name}</h3>
              <a 
                href={`https://maps.google.com/?q=${encodeURIComponent(location.address || location.name)}`} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-xs text-blue-500 hover:underline block truncate" 
                title={location.address || "View on Google Maps"}
              >
                {location.address || "No address"}
              </a>
            </EditorInteractable>
          )}
        </div>
      </div>

      {/* The right column for action buttons is now directly in LocationInfoWindowContent */}
      {/* This component no longer renders those buttons or their popovers/menus. */}
    </CardHeader>
  );
});

LocationHeader.displayName = 'LocationHeader'; 